import { Link } from "react-router-dom";
import { ArrowUpRight, Phone, ImageIcon } from "lucide-react";
import { PROJECTS } from "@/data/projects";
import { SITE } from "@/lib/site";
import Seo from "@/components/Seo";

function ProjectCard({ project }) {
  return (
    <Link
      to={`/projects/${project.slug}`}
      className="group block"
      data-testid={`project-card-${project.slug}`}
    >
      <div className="aspect-[4/3] bg-[#F8F8F5] border border-[#1A1A1A]/8 overflow-hidden">
        {project.coverImage ? (
          <img
            src={project.coverImage}
            alt={project.title}
            className="w-full h-full object-cover object-center transition-transform duration-700 ease-out group-hover:scale-[1.02]"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-center px-6">
            <ImageIcon
              className="w-10 h-10 text-[#1A1A1A]/15"
              strokeWidth={1.25}
            />
            <span className="mt-4 text-[10px] uppercase tracking-[0.22em] text-[#1A1A1A]/30 font-semibold">
              Project Photo
            </span>
          </div>
        )}
      </div>
      <div className="mt-6">
        <h3
          className="font-heading text-xl sm:text-2xl tracking-tight text-[#1A1A1A] leading-tight group-hover:text-[#F97316] transition-colors"
          style={{ fontWeight: 800 }}
        >
          {project.title}
        </h3>
        <p className="mt-3 text-[15px] text-[#2B2F33]/80 leading-relaxed">
          {project.description}
        </p>
        <span
          className="inline-flex items-center gap-1.5 mt-4 text-xs font-bold uppercase tracking-[0.18em] text-[#F97316] group-hover:gap-2.5 transition-all"
          data-testid={`project-learn-more-${project.slug}`}
        >
          View project <ArrowUpRight className="w-3.5 h-3.5" />
        </span>
      </div>
    </Link>
  );
}

export default function Projects() {
  return (
    <div data-testid="projects-page" className="bg-white">
      <Seo
        path="/projects"
        title="Project Examples"
        description="Examples of the types of sign projects Todd Friedman helps customers navigate throughout Richmond and Central Virginia — new builds, repairs, branding updates, and receive-and-install projects."
      />

      {/* INTRO */}
      <section className="pt-20 pb-14 lg:pt-28 lg:pb-20 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-7">
            <span className="w-10 h-0.5 bg-[#F97316]" />
            <span className="text-[11px] tracking-[0.22em] uppercase font-bold text-[#F97316]">
              Projects Around Richmond
            </span>
          </div>
          <h1
            className="font-heading text-[#1A1A1A] tracking-tight leading-[0.98]"
            style={{ fontWeight: 900, fontSize: "clamp(2.75rem, 5.5vw, 5rem)" }}
            data-testid="projects-headline"
          >
            Project Examples
          </h1>
          <div className="mt-10 max-w-2xl space-y-5 text-lg text-[#2B2F33]/85 leading-relaxed">
            <p>
              Every sign project is different. Here are a few examples of the
              types of projects I help customers navigate throughout Richmond
              and Central Virginia.
            </p>
            <p>
              Most people don&rsquo;t spend much time thinking about signs until
              they need one. These examples show the kinds of signage projects
              businesses, property managers, contractors, and organizations
              encounter every day.
            </p>
            <p>
              Whether it&rsquo;s a new sign, a repair, a branding update, or a
              receive-and-install project, the goal is usually the same: get
              from an idea to a completed installation.
            </p>
          </div>
        </div>
      </section>

      {/* PROJECT GRID */}
      <section className="pb-24 lg:pb-32 bg-white" data-testid="projects-grid">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14 lg:gap-x-10 lg:gap-y-20">
            {PROJECTS.map((p) => (
              <ProjectCard key={p.slug} project={p} />
            ))}
          </div>
        </div>
      </section>

      {/* BOTTOM CTA */}
      <section
        className="border-t border-[#1A1A1A]/10 bg-[#F8F8F5] py-20 lg:py-28"
        data-testid="projects-cta"
      >
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <h2
            className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#1A1A1A] leading-[1.05]"
            style={{ fontWeight: 900 }}
          >
            Need a sign project?
          </h2>
          <p className="mt-6 text-lg text-[#2B2F33]/80 leading-relaxed max-w-xl mx-auto">
            Whether you&rsquo;re opening a new location, replacing an existing
            sign, or just trying to understand your options, I&rsquo;m happy to
            help if I can.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link
              to="/contact"
              data-testid="projects-cta-primary"
              className="bg-[#F97316] hover:bg-[#EA580C] text-white text-sm font-bold uppercase tracking-wider px-7 py-4 inline-flex items-center gap-2 transition-colors"
            >
              Talk To Todd <ArrowUpRight className="w-4 h-4" />
            </Link>
            <a
              href={SITE.phoneHref}
              data-testid="projects-cta-call"
              className="border-2 border-[#1A1A1A]/15 hover:border-[#1A1A1A] text-[#1A1A1A] text-sm font-bold uppercase tracking-wider px-7 py-4 inline-flex items-center gap-2 transition-colors"
            >
              <Phone className="w-4 h-4" /> Call {SITE.phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
